import { useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, Receipt, Wallet } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import { formatMoney } from '@/client/types/payment';
import {
  useStorePayoutBalanceQuery,
  useStoreTransactionListQuery,
  useStoreTransactionSummaryQuery,
  useStoreWalletQuery,
} from '@/client/api/stores/parity.query';
import { cn } from '@/lib/utils';

import { Pager, Pill, RangePicker, SectionCard, StatTile, label, money, rangeFor, shortDate, statusTone, useRangeParam, TableSkeleton, ListSkeleton } from './shared';
import { ExportButton } from './ExportButton';

const LIMIT = 25;
const KINDS = ['sale', 'store_plan', 'refund', 'payout', 'fee', 'adjustment'];
const STATUSES = ['successful', 'pending', 'unsuccessful', 'refunded'];

const WalletLedger: React.FC<{ storeid: number }> = ({ storeid }) => {
  const [offset, setOffset] = useState(0);
  const { data, isLoading } = useStoreWalletQuery(storeid, { limit: 15, offset });
  const rows = data?.transactions ?? [];

  return (
    <SectionCard title="Wallet ledger" description="Credits from paid orders, debits for refunds and payouts, holds.">
      {isLoading ? (
        <ListSkeleton rows={6} media="avatar" />
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-400">No wallet activity.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {rows.map((w) => {
            const credit = w.type === 'credit' || w.type === 'release';
            return (
              <div key={w.wtid} className="flex items-center gap-3 py-2 text-sm">
                {credit ? <ArrowDownLeft className="h-4 w-4 text-emerald-600" /> : <ArrowUpRight className="h-4 w-4 text-red-500" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-gray-800">{w.description || label(w.reference_type)}</p>
                  <p className="text-xs text-gray-400">{shortDate(w.date_added)} · {w.type}</p>
                </div>
                <span className={cn('tabular-nums font-medium', credit ? 'text-emerald-700' : 'text-red-600')}>
                  {credit ? '+' : '−'}{money(Math.abs(w.amount))}
                </span>
                <span className="w-24 text-right text-xs tabular-nums text-gray-500">{money(w.balance_after)}</span>
              </div>
            );
          })}
        </div>
      )}
      <Pager total={data?.total_count ?? 0} limit={15} offset={offset} onChange={setOffset} />
    </SectionCard>
  );
};

/**
 * The store's money, exactly as the seller's Transactions tab reports it
 * (sales linked through the order invoice or listing, store-plan charges,
 * refunds, fees and payouts), plus the wallet ledger and balance.
 */
export const TransactionsTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const [range, setRange] = useRangeParam('30d');
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const dates = rangeFor(range);

  const { data: summary } = useStoreTransactionSummaryQuery(store.storeid, dates);
  const { data: balance } = useStorePayoutBalanceQuery(store.storeid);
  const { data, isLoading, isFetching } = useStoreTransactionListQuery(store.storeid, {
    ...dates, kind: kind || undefined, status: status || undefined, limit: LIMIT, offset,
  });
  const txns = data?.transactions ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <RangePicker value={range} onChange={(k) => { setRange(k); setOffset(0); }} />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Income" value={money(summary?.income)} icon={<Receipt className="h-4 w-4 text-emerald-600" />}
          sub={summary ? `${summary.successful_count} successful` : undefined} />
        <StatTile label="Refunds & fees" value={money((summary?.refunds ?? 0) + (summary?.fees ?? 0))}
          sub={summary ? `${summary.failed_count} failed · ${summary.pending_count} pending` : undefined} />
        <StatTile label="Net" value={money(summary?.net)} sub={summary ? `plan ${money(summary.store_plan)} · payouts ${money(summary.payouts)}` : undefined} />
        <StatTile label="Wallet balance" value={balance ? formatMoney(balance.balance, balance.ccode) : '—'}
          icon={<Wallet className="h-4 w-4 text-blue-600" />} sub={balance?.wid ? `wallet #${balance.wid}` : 'no wallet yet'} />
      </div>

      <SectionCard
        actions={
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{isFetching ? 'Refreshing…' : `${data?.total_count ?? 0} in range`}</span>
            <ExportButton storeid={store.storeid} resource="transactions" params={{ ...dates, kind: kind || undefined, status: status || undefined }} />
            <select value={kind} onChange={(e) => { setKind(e.target.value); setOffset(0); }} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs" aria-label="Kind">
              <option value="">All kinds</option>
              {KINDS.map((k) => <option key={k} value={k}>{label(k)}</option>)}
            </select>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs" aria-label="Status">
              <option value="">All statuses</option>
              {STATUSES.map((k) => <option key={k} value={k}>{label(k)}</option>)}
            </select>
          </div>
        }
      >
        {isLoading ? (
          <TableSkeleton cols={6} />
        ) : txns.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">No transactions in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-400">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Kind</th>
                  <th className="py-2 pr-4">Description</th>
                  <th className="py-2 pr-4 text-right">Amount</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {txns.map((t) => (
                  <tr key={t.tid}>
                    <td className="py-2 pr-4 text-gray-600">{shortDate(t.date_added)}</td>
                    <td className="py-2 pr-4 capitalize text-gray-700">{label(t.kind)}</td>
                    <td className="max-w-[320px] truncate py-2 pr-4 text-gray-700">
                      {t.description ?? t.listing_name ?? t.plan_name ?? `#${t.tid}`}
                      {t.orderid ? <span className="ml-1 text-xs text-gray-400">· order #{t.orderid}</span> : null}
                    </td>
                    <td className={cn('py-2 pr-4 text-right tabular-nums font-medium', t.signed_amount < 0 ? 'text-red-600' : 'text-gray-900')}>
                      {formatMoney(t.signed_amount, t.ccode ?? 'GHS')}
                    </td>
                    <td className="py-2"><Pill tone={statusTone(t.status)}>{label(t.status)}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager total={data?.total_count ?? 0} limit={LIMIT} offset={offset} onChange={setOffset} />
      </SectionCard>

      <WalletLedger storeid={store.storeid} />
    </div>
  );
};
